import { ActivityStatusPill } from "@/modules/dashboard/payments-activity/components/ActivityStatusPill";
import { BUCKET_BADGE_VARIANT } from "@/modules/dashboard/payments-activity/constants/paymentsActivity.constants";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { SWAP_STATUS_LABEL_KEYS } from "../constants/paymentsLedger.constants";
import type { LedgerOperationCandidate } from "../types/paymentsLedger.types";

export interface LedgerStatusPillProps {
  candidate: LedgerOperationCandidate;
  className?: string;
}

/**
 * Status badge for any of the three operation kinds.
 *
 * Payins and payouts reuse the payments-activity badge, so their wording and their look match that surface
 * exactly. Swaps do not: their vocabulary (CREATED / COMPLETED / FAILED) is not in the activity label map,
 * and reusing it would render a raw enum value — so the swap labels live in this namespace and are applied
 * here, over the same bucket colors and the same in-flight dot.
 */
export function LedgerStatusPill({ candidate, className }: LedgerStatusPillProps) {
  const { t } = useTranslations("paymentsLedger");

  if (candidate.kind !== "swaps") {
    return <ActivityStatusPill row={candidate} className={className} />;
  }

  const labelKey = SWAP_STATUS_LABEL_KEYS[candidate.status];
  return (
    <ColocaBadge variant={BUCKET_BADGE_VARIANT[candidate.bucket]} className={className}>
      <span
        aria-hidden="true"
        className={cn(
          "mr-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current",
          candidate.isNonTerminal && "animate-pulse",
        )}
      />
      {labelKey ? t(labelKey) : candidate.status}
    </ColocaBadge>
  );
}
